/**
 * Per-type cell editors, populated from the board's real column settings (REQ-6.2):
 * status/priority use the board's labels in the board's colours, people list real
 * users, dropdowns list the real options — the user is never asked to type a raw
 * value monday would reject (REQ-6.3).
 *
 * Draft value conventions are those of lib/columnValues.js.
 */
import { Avatar, Button, Checkbox, DatePicker, Dropdown, RadioButton, Tab, TabList, TextField } from '@vibe/core';
import { CloseSmall } from '@vibe/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { canonicalType } from '../../Config';
import { mentionsMissingScope, PERMISSION_HINT } from '../../lib/captureGroup';
import { isValidLinkUrl } from '../../lib/columnValues';
import {
  addDays,
  applyBaseDate,
  DEFAULT_TIMELINE_RULE,
  DYNAMIC_PRESETS,
  formatDate,
  isDynamicRule,
  isTimelineRule,
  MAX_OFFSET,
  parentCell,
  parentColumnLabel,
  parentDate,
  resolveDate,
  resolvePeople,
  resolveTimeline,
  TIMELINE_PRESETS,
  timelinePreset,
  usesParent,
} from '../../lib/dynamicValues';
import { RatingSymbol, ratingSettings } from '../RatingSymbols';
import Spinner from '../Spinner';
import AnchoredPopover from './AnchoredPopover';
import FileCellEditor from './FileCellEditor';

function StatusEditor({ column, value, onChange, onClose }) {
  const labels = (column.settings && column.settings.labels) || [];
  if (!labels.length) {
    return (
      <div className="spt-menu-empty">
        This column is not on the current board, so its labels are unknown here. Values can
        still be kept or cleared.
      </div>
    );
  }
  return (
    <>
      <div className="spt-statuslist">
        {labels.map((l) => (
          <button
            key={l.index}
            type="button"
            className={`spt-statusopt ${value && value.index === l.index ? 'spt-statusopt-on' : ''}`}
            style={{ backgroundColor: l.color }}
            onClick={() => {
              onChange({ index: l.index, label: l.label });
              onClose();
            }}
          >
            {l.label}
          </button>
        ))}
      </div>
      <Button
        kind="tertiary"
        size="small"
        leftIcon={CloseSmall}
        onClick={() => {
          onChange(null);
          onClose();
        }}
      >
        Clear
      </Button>
    </>
  );
}

function PeopleEditor({ users, usersError, usersLoading, onNeedUsers, value, onChange, onClose, parentColumns, parent }) {
  // Members are fetched lazily — the first People cell opened asks for them, and
  // the loader ignores the call when they are already loading or in hand.
  useEffect(() => {
    if (onNeedUsers) onNeedUsers();
  }, [onNeedUsers]);
  const persons = (value && value.persons) || [];
  const stored = value && value.dynamic;
  // "Dynamic": copy the parent's People column (lib/dynamicValues isPeopleRule), beside the picks
  const [tab, setTab] = useState(stored ? 'dynamic' : 'pick');
  const peopleColumns = useMemo(() => (parentColumns || []).filter((c) => canonicalType(c.type) === 'people'), [parentColumns]);
  const columnOptions = useMemo(() => peopleColumns.map((c) => ({ value: c.title, label: c.title })), [peopleColumns]);
  const [rule, setRule] = useState(() =>
    stored
      ? { column: stored.column, alsoPicked: stored.alsoPicked !== false }
      : { column: peopleColumns.length ? { title: peopleColumns[0].title, type: 'people' } : null, alsoPicked: persons.length > 0 },
  );
  // opened before the parent board's columns arrived: its first People column, once they are in
  const ruleColumn = rule.column || (peopleColumns.length ? { title: peopleColumns[0].title, type: 'people' } : null);

  let picker;
  if (usersLoading) {
    picker = <div className="spt-menu-empty">Loading board members…</div>;
  } else if (!users || !users.length) {
    // An API failure and a genuinely empty board look identical in the list, so
    // say which one it is — monday returns scope errors as HTTP 200 + errors[],
    // and "no members" was hiding them.
    picker = usersError ? (
      <div className="spt-menu-empty">
        {mentionsMissingScope(usersError)
          ? `Could not load this board's members: this version of the app needs a permission that hasn’t been approved yet. ${PERMISSION_HINT}`
          : `Could not load this board's members. monday said: “${usersError}”.`}
      </div>
    ) : (
      <div className="spt-menu-empty">
        No members found on this board. The picker only offers people who are
        subscribed to the board, since only they can be assigned. Add them to the
        board first, then reopen the editor.
      </div>
    );
  } else {
    const options = users.map((u) => ({ value: u.id, label: u.name }));
    // a person no longer on the board stays picked under their stored name
    const picked = persons.map((p) => options.find((o) => String(o.value) === String(p.id)) || { value: p.id, label: p.name });
    picker = (
      <Dropdown
        className="spt-boxdropdown"
        multi
        searchable
        boxMode
        autoFocus
        size="small"
        aria-label="People"
        placeholder="Search names"
        noOptionsMessage="No matching members"
        options={options}
        value={picked}
        onChange={(items) => {
          const next = (items || []).map((o) => ({ id: o.value, name: o.label }));
          // picks apply at once; a rule already set stays beside them
          onChange(next.length || stored ? { ...(value || {}), persons: next } : null);
        }}
      />
    );
  }
  // only a subitem has a parent item; elsewhere the cell is today's picker alone
  if (!parentColumns) return picker;

  const fullRule = ruleColumn ? { base: 'parent', column: ruleColumn, alsoPicked: rule.alsoPicked } : null;
  const own = { value: persons.length ? JSON.stringify({ personsAndTeams: persons.map((p) => ({ id: Number(p.id), kind: 'person' })) }) : null, text: persons.map((p) => p.name).join(', ') };
  let preview = null;
  if (fullRule) {
    if (!parent) preview = { note: 'Open the editor from an item to preview this on it.' };
    else if (!parentCell(parent, fullRule.column)) preview = { note: `This item has no ${fullRule.column.title} column, so only the people picked would be set.` };
    else {
      const r = resolvePeople(fullRule, parent, own);
      preview = r ? { names: r.text.split(', ').filter(Boolean) } : { note: 'Nobody on this item, and nobody picked: the cell would be left empty.' };
    }
  }

  return (
    <div className="spt-dateeditor">
      <DateTabs tab={tab} setTab={setTab} pickLabel="Pick people" />
      {tab === 'pick' ? (
        picker
      ) : (
        <>
          <div className="spt-dateeditor-options" role="group" aria-label="Dynamic people">
            {columnOptions.length === 0 ? (
              <p className="spt-dateeditor-hint spt-dateeditor-hint-flush">The parent board has no People column.</p>
            ) : (
              <>
                <div className="spt-dateeditor-grid spt-dateeditor-grid-flush">
                  <span className="spt-dateeditor-key">Parent Item Column</span>
                  <SelectField label="Parent column" value={ruleColumn.title} options={columnOptions} width={200} onChange={(t) => setRule({ ...rule, column: { title: t, type: 'people' } })} />
                </div>
                <Checkbox
                  className="spt-checkbox"
                  checkboxClassName="spt-checkbox-box"
                  label="Also add the people picked"
                  checked={rule.alsoPicked}
                  onChange={(e) => setRule({ ...rule, alsoPicked: Boolean(e.target.checked) })}
                />
                <p className="spt-dateeditor-hint">{persons.length ? persons.map((p) => p.name).join(', ') : 'Nobody picked yet: pick people on the first tab.'}</p>
                <p className="spt-dateeditor-hint spt-dateeditor-hint-flush">If the parent's column is empty, this cell is left empty.</p>
              </>
            )}
          </div>
          <div className="spt-dateeditor-footer">
            {preview && preview.note && <p className="spt-dateeditor-preview">{preview.note}</p>}
            {preview && preview.names && (
              <p className="spt-dateeditor-preview spt-people-preview">
                <span>Preview on this item:</span>
                <span className="spt-avatars">
                  {preview.names.map((n) => (
                    <Avatar key={n} size="small" type="text" text={initialsOf(n)} aria-label={n} />
                  ))}
                </span>
                <span>{preview.names.join(', ')}</span>
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                kind="tertiary"
                size="small"
                onClick={() => {
                  onChange(null);
                  onClose();
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                disabled={!fullRule}
                onClick={() => {
                  onChange({ persons, dynamic: fullRule });
                  onClose();
                }}
              >
                Set
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** "AK" for Anna Kim: the preview's avatars. */
const initialsOf = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

function DropdownEditor({ column, value, onChange }) {
  const options = ((column.settings && column.settings.options) || []).map((opt) => ({
    value: opt.id ?? opt.name,
    label: opt.name,
    id: opt.id,
  }));
  // a label the column no longer offers stays picked
  const picked = ((value && value.labels) || []).map((l) => options.find((o) => o.label === l) || { value: l, label: l });
  if (!options.length) {
    return (
      <div className="spt-menu-empty">
        This column is not on the current board, so its options are unknown here.
      </div>
    );
  }
  return (
    <Dropdown
      className="spt-boxdropdown"
      multi
      searchable
      boxMode
      autoFocus
      size="small"
      aria-label={column.title}
      placeholder="Search options"
      noOptionsMessage="No matching options"
      options={options}
      value={picked}
      onChange={(items) => {
        const list = items || [];
        const ids = list.map((o) => o.id).filter((i) => i !== undefined);
        onChange(list.length ? { ids, labels: list.map((o) => o.label) } : null);
      }}
    />
  );
}

// Picked dates use monday's calendar (Vibe DatePicker) rather than the browser's
// date field, whose text follows the OS region ("dd/mm/yyyy") and so never
// matched the cells. The calendar has no text; the line under it and the cells
// read "Oct 23, 2026" (formatDate).
/** 'YYYY-MM-DD' → the calendar's Date, at local midnight so the day never shifts; else undefined. */
const toDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 ? d : undefined; // not 2026-02-30
};
const pad2 = (n) => String(n).padStart(2, '0');
/** The calendar's Date → 'YYYY-MM-DD' of its local day; '' for none. */
const fromDate = (d) => (d instanceof Date && !Number.isNaN(d.getTime()) ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : '');
/**
 * The day a click on Vibe's range calendar landed on. The calendar hands back
 * the range it would make (extending or trimming the old one), not the day, so
 * the day is whichever end is new; re-clicking a picked day changes neither, and
 * a cleared range means its start was clicked.
 */
const clickedDay = (prev, from, to) => {
  const isNew = (d) => d && d !== prev.from && d !== prev.to;
  if (isNew(from)) return from;
  if (isNew(to)) return to;
  return to || from || prev.from;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const weekdayOf = (iso) => WEEKDAYS[new Date(`${iso}T00:00:00Z`).getUTCDay()];
const clampOffset = (n) => Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, n));
/** What was typed as a whole number ("-3", "05"), or null: '' (also what a number field reports for "1."), "1.5". */
const wholeNumber = (raw) => (/^-?\d+$/.test(String(raw).trim()) ? Number(raw) : null);

/**
 * One choice from a short fixed list: monday's small dropdown field, `width`
 * px wide. The options are copied: Vibe's Dropdown stamps its own `index` on
 * each option object, so two dropdowns sharing one object (the parent columns
 * in both ends of a timeline) would pick each other's neighbours.
 */
function SelectField({ label, value, options, width, onChange }) {
  const own = useMemo(() => options.map((o) => ({ ...o })), [options]);
  return (
    <span className="spt-selectfield" style={{ width }}>
      <Dropdown
        size="small"
        clearable={false}
        aria-label={label}
        options={own}
        value={own.find((o) => o.value === value)}
        onChange={(o) => o && onChange(o.value)}
      />
    </span>
  );
}

const UNIT_OPTIONS = [
  { value: 'days', label: 'days' },
  { value: 'working_days', label: 'working days' },
];
const END_BASE_OPTIONS = [
  { value: 'start', label: 'Start' },
  { value: 'apply', label: 'Today' },
];

/**
 * The parent board's Date columns, and each Timeline column's start and end,
 * as select options: { value, label, column } where column is the rule's
 * { title, type, part? } (lib/dynamicValues isParentColumn).
 */
function parentDateOptions(parentColumns) {
  const out = [];
  for (const c of parentColumns || []) {
    const type = canonicalType(c.type);
    if (type === 'date') out.push({ value: `parent|date|${c.title}`, label: c.title, column: { title: c.title, type: 'date' } });
    if (type === 'timeline') {
      for (const part of ['start', 'end']) {
        out.push({ value: `parent|timeline|${c.title}|${part}`, label: `${c.title} (${part})`, column: { title: c.title, type: 'timeline', part } });
      }
    }
  }
  return out;
}
const parentOptionValue = (column) =>
  column ? (column.type === 'timeline' ? `parent|timeline|${column.title}|${column.part}` : `parent|date|${column.title}`) : '';

/**
 * What the footer says about a rule that counts from the parent, on the
 * current item: a prefix naming the parent's date ("Preview on this item
 * (Start date Fri Nov 20): "), or a note saying why there is no preview.
 */
function parentPreview(column, parent) {
  if (!parent) return { note: 'Open the editor from an item to preview where this lands.' };
  if (!parentCell(parent, column)) return { note: `This item has no ${parentColumnLabel(column)} column, so this cell would be left empty.` };
  const d = parentDate(parent, column);
  if (!d) return { note: `This item's ${parentColumnLabel(column)} is empty, so this cell would be left empty.` };
  return { prefix: `Preview on this item (${parentColumnLabel(column)} ${weekdayOf(d)} ${formatDate(d).replace(/, \d{4}$/, '')}): ` };
}

/** The same for a timeline rule: the first end that counts from the parent decides the note. */
function timelineParentPreview(rule, parent) {
  for (const end of [rule.start, rule.end]) {
    if (end.base !== 'parent') continue;
    const p = parentPreview(end.column, parent);
    if (p.note) return p;
  }
  return { prefix: 'Preview on this item: ' };
}

/**
 * "[+3] [days | working days]": the signed offset and unit every dynamic date
 * row shares. The number field's own stepper goes below zero; a positive
 * offset shows its "+" inside the field (a number input can't hold one).
 */
function OffsetFields({ label, offset, unit, min = -MAX_OFFSET, onChange }) {
  // What was typed stays in the field; only a whole number becomes the offset,
  // anything else (empty, "1.5") makes it null, so the editor says so and Set
  // waits. A number from outside (or clamped to the limit) replaces the text.
  const [typed, setTyped] = useState(null);
  const shown = typed !== null && (offset === null || wholeNumber(typed) === offset) ? typed : offset === null ? '' : String(offset);
  return (
    <>
      <span className="spt-field spt-numfield">
        {offset > 0 && <span aria-hidden="true">+</span>}
        <input
          type="number"
          min={min}
          max={MAX_OFFSET}
          step={1}
          value={shown}
          aria-label={`${label} offset`}
          aria-invalid={offset === null || undefined}
          onFocus={() => onChange({ offset, unit })}
          onChange={(e) => {
            const n = wholeNumber(e.target.value);
            setTyped(e.target.value);
            onChange({ offset: n === null ? null : clampOffset(n), unit });
          }}
        />
      </span>
      <SelectField label={`${label} unit`} value={unit} options={UNIT_OPTIONS} width={136} onChange={(u) => onChange({ offset, unit: u })} />
    </>
  );
}

/** monday's own tabs over the date and timeline editors. */
function DateTabs({ tab, setTab, pickLabel }) {
  return (
    <TabList
      className="spt-dateeditor-tabs"
      size="sm"
      activeTabId={tab === 'pick' ? 0 : 1}
      onTabChange={(i) => setTab(i === 0 ? 'pick' : 'dynamic')}
    >
      <Tab>{pickLabel}</Tab>
      <Tab>Dynamic</Tab>
    </TabList>
  );
}

const WEEK_HEAD = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MAX_STRIP_WEEKS = 6;
const mondayIndex = (iso) => (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7;
const daysBetween = (a, b) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);

/**
 * Where a rule lands if applied today, as Monday-first weeks: the landed days
 * filled, today outlined, weekends muted — so "working days" skipping a
 * weekend is visible at a glance. The weeks run from whichever of today and
 * the range comes first to whichever comes last; past six weeks only the
 * first and last two are drawn, with the gap counted between them.
 */
function WeekStrip({ from, to, today, caption }) {
  const lo = [from, today].sort()[0];
  const hi = [to, today].sort()[1];
  const first = addDays(lo, -mondayIndex(lo));
  const count = Math.floor(daysBetween(first, hi) / 7) + 1;
  const week = (w) => Array.from({ length: 7 }, (_, d) => addDays(first, w * 7 + d));
  const all = Array.from({ length: count }, (_, w) => w);
  const shown = count > MAX_STRIP_WEEKS ? [0, 1, null, count - 2, count - 1] : all;
  return (
    <div className="spt-weekstrip" role="img" aria-label={caption}>
      {WEEK_HEAD.map((h, i) => (
        <span key={`h${i}`} className={`spt-weekstrip-head${i > 4 ? ' is-weekend' : ''}`}>
          {h}
        </span>
      ))}
      {shown.map((w) =>
        w === null ? (
          <span key="gap" className="spt-weekstrip-gap">
            ⋯ {count - 4} more weeks
          </span>
        ) : (
          week(w).map((iso, d) => {
            const cls = ['spt-weekstrip-day'];
            if (d > 4) cls.push('is-weekend');
            if (iso >= from && iso <= to) cls.push('is-on');
            if (iso === today) cls.push('is-today');
            return (
              <span key={iso} className={cls.join(' ')} title={`${weekdayOf(iso)} ${formatDate(iso)}`}>
                {Number(iso.slice(8))}
              </span>
            );
          })
        ),
      )}
    </div>
  );
}

/** Where the rule lands today (a caption and a week strip), then Clear and Set. */
function DynamicFooter({ lands, today, invalid, canSet, onClear, onSet, prefix = 'Preview: ', note }) {
  const caption = note
    ? note
    : lands
      ? lands.from === lands.to
        ? `${prefix}${weekdayOf(lands.from)} ${formatDate(lands.from)}`
        : `${prefix}${formatDate(lands.from).replace(/, \d{4}$/, '')} – ${formatDate(lands.to)}`
      : invalid;
  return (
    <div className="spt-dateeditor-footer">
      <p className="spt-dateeditor-preview">{caption}</p>
      {lands && <WeekStrip from={lands.from} to={lands.to} today={today} caption={caption} />}
      <div className="flex justify-end gap-2">
        <Button kind="tertiary" size="small" onClick={onClear}>
          Clear
        </Button>
        <Button size="small" disabled={!canSet} onClick={onSet}>
          Set
        </Button>
      </div>
    </div>
  );
}

/**
 * A date cell holds either a fixed date ('YYYY-MM-DD') or a rule that resolves
 * when the template is applied ({ dynamic: { base, offset, unit } }, see
 * lib/dynamicValues). Two tabs: Pick a date, and Dynamic with Today / Tomorrow /
 * Yesterday or "Today [+n] [days | working days]", plus a preview of where the
 * rule lands today. "Today" is the apply date.
 */
function DateEditor({ value, onChange, onClose, parentColumns, parent }) {
  const isDynamic = Boolean(value && typeof value === 'object' && value.dynamic);
  const [tab, setTab] = useState(isDynamic ? 'dynamic' : 'pick');
  const [rule, setRule] = useState(isDynamic ? value.dynamic : DYNAMIC_PRESETS[0].rule);
  const isPreset = (r) => r.base === 'apply' && DYNAMIC_PRESETS.some((p) => p.rule.offset === r.offset && p.rule.unit === r.unit);
  // the custom row keeps its own number and unit so picking a preset never loses them
  const [custom, setCustom] = useState(rule.base === 'apply' && !isPreset(rule) ? { offset: rule.offset, unit: rule.unit } : { offset: 5, unit: 'days' });
  // "From the parent item": the parent board's date columns; its row keeps its own column and offset too
  const parentOptions = useMemo(() => parentDateOptions(parentColumns), [parentColumns]);
  const [parentRule, setParentRule] = useState(() =>
    rule.base === 'parent'
      ? { column: rule.column, offset: rule.offset, unit: rule.unit }
      : { column: parentOptions.length ? parentOptions[0].column : null, offset: 0, unit: 'days' },
  );
  // opened before the parent board's columns arrived: its first date column, once they are in
  const parentPick = parentRule.column || !parentOptions.length ? parentRule : { ...parentRule, column: parentOptions[0].column };

  const today = applyBaseDate(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const lands = resolveDate(rule, { baseDate: today, parent });
  const selectedKey =
    rule.base === 'parent'
      ? 'parent'
      : isPreset(rule)
        ? DYNAMIC_PRESETS.find((p) => p.rule.offset === rule.offset && p.rule.unit === rule.unit).key
        : 'custom';
  const preview = rule.base === 'parent' ? parentPreview(rule.column, parent) : {};
  // a parent rule is set on its own terms: where it lands depends on the item it is applied to
  const canSet = rule.base === 'parent' ? isDynamicRule(rule) : Boolean(lands);

  const pickPreset = (p) => setRule(p.rule);
  const pickCustom = (c) => {
    setCustom(c);
    setRule({ base: 'apply', ...c });
  };
  const pickParent = (p) => {
    setParentRule(p);
    setRule({ base: 'parent', ...p });
  };
  const clear = () => {
    onChange(null);
    onClose();
  };
  const picked = typeof value === 'string' ? value : '';

  return (
    <div className="spt-dateeditor">
      <DateTabs tab={tab} setTab={setTab} pickLabel="Pick a date" />

      {tab === 'pick' ? (
        <>
          <DatePicker date={toDate(picked)} onDateChange={(d) => onChange(fromDate(d) || null)} />
          <div className="flex items-center justify-between">
            <p className="spt-dateeditor-preview">{picked ? formatDate(picked) : 'No date picked'}</p>
            <Button kind="tertiary" size="small" leftIcon={CloseSmall} onClick={clear}>
              Clear
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="spt-dateeditor-options" role="radiogroup" aria-label="Dynamic date">
            {DYNAMIC_PRESETS.map((p) => (
              <RadioButton key={p.key} name="spt-dyn" text={p.label} checked={selectedKey === p.key} onSelect={() => pickPreset(p)} />
            ))}
            <div className="spt-dateeditor-row">
              <RadioButton name="spt-dyn" text="Today" checked={selectedKey === 'custom'} onSelect={() => pickCustom(custom)} />
              <OffsetFields label="Date" offset={custom.offset} unit={custom.unit} onChange={pickCustom} />
            </div>
            {parentColumns && (
              <>
                {parentOptions.length === 0 ? (
                  <p className="spt-dateeditor-hint">The parent board has no Date or Timeline column.</p>
                ) : (
                  <>
                    <RadioButton name="spt-dyn" text="Parent item's date" checked={selectedKey === 'parent'} onSelect={() => pickParent(parentPick)} />
                    <div className="spt-dateeditor-grid">
                      <span className="spt-dateeditor-key">Column</span>
                      <SelectField
                        label="Parent column"
                        value={parentOptionValue(parentPick.column)}
                        options={parentOptions}
                        width={200}
                        onChange={(v) => pickParent({ ...parentPick, column: parentOptions.find((o) => o.value === v).column })}
                      />
                      <span className="spt-dateeditor-key">Offset</span>
                      <span className="spt-dateeditor-row">
                        <OffsetFields label="Parent" offset={parentPick.offset} unit={parentPick.unit} onChange={(p) => pickParent({ ...parentPick, ...p })} />
                      </span>
                    </div>
                    <p className="spt-dateeditor-hint">If the parent's date is empty, this cell is left empty.</p>
                  </>
                )}
              </>
            )}
          </div>
          <DynamicFooter
            lands={lands ? { from: lands, to: lands } : null}
            today={today}
            invalid="Choose a whole number of days."
            canSet={canSet}
            prefix={preview.prefix}
            note={preview.note}
            onClear={clear}
            onSet={() => {
              onChange({ dynamic: rule });
              onClose();
            }}
          />
        </>
      )}
    </div>
  );
}

/**
 * A timeline holds either two fixed dates or a rule for each end, resolved when
 * the template is applied (lib/dynamicValues). The Dynamic tab offers Next week
 * and Next month (Today → +7 / +30 days), or Custom, which gives each end the
 * date column's own control — a base, a signed number and days / working days:
 *   Start  Today            [0]  [days]
 *   End    [Start | Today]  [+4] [working days]
 */
function TimerangeEditor({ value, onChange, onClose, parentColumns, parent }) {
  const isDynamic = Boolean(value && typeof value === 'object' && value.dynamic);
  const [tab, setTab] = useState(isDynamic ? 'dynamic' : 'pick');
  const [rule, setRule] = useState(() => {
    const r = isDynamic ? value.dynamic : DEFAULT_TIMELINE_RULE;
    return { start: { ...r.start }, end: { ...r.end } };
  });
  // a stored custom rule that happens to equal a preset still opens as that preset
  const [mode, setMode] = useState(() => (isDynamic && timelinePreset(value.dynamic) ? timelinePreset(value.dynamic).key : 'custom'));
  // Custom keeps its own rule so trying a preset never loses it
  const [custom, setCustom] = useState(rule);
  // The dates as picked: the calendar's first click starts a range, the second
  // ends it on or after the start. Only a whole range is handed on, so the cell
  // keeps its last range until the new one has an end. A range that ends before
  // it starts (stored before the calendar) says so.
  const [range, setRange] = useState(() => (!isDynamic && value ? value : { from: '', to: '' }));
  const backwards = Boolean(range.from && range.to && range.from > range.to);
  const pickRange = ({ date, endDate }) => {
    const day = clickedDay(range, fromDate(date), fromDate(endDate));
    const next = range.from && !range.to && day >= range.from ? { from: range.from, to: day } : { from: day, to: '' };
    setRange(next);
    if (next.to) onChange(next);
  };
  const clearRange = () => {
    onChange(null);
    onClose();
  };

  const today = applyBaseDate(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const lands = resolveTimeline(rule, { baseDate: today, parent });
  const preview = usesParent(rule) ? timelineParentPreview(rule, parent) : {};
  const canSet = usesParent(rule) ? isTimelineRule(rule) : Boolean(lands);
  // either end can count from the parent item's date, when the editor knows the parent board
  const parentOptions = useMemo(() => parentDateOptions(parentColumns), [parentColumns]);
  const startOptions = useMemo(() => [{ value: 'apply', label: 'Today' }, ...parentOptions], [parentOptions]);
  const endOptions = useMemo(() => [...END_BASE_OPTIONS, ...parentOptions], [parentOptions]);
  const baseValue = (r) => (r.base === 'parent' ? parentOptionValue(r.column) : r.base);
  const basePatch = (v, options) => {
    const o = options.find((x) => x.value === v);
    return o && o.column ? { base: 'parent', column: o.column } : { base: v };
  };
  const pickPreset = (p) => {
    setMode(p.key);
    setRule({ start: { ...p.rule.start }, end: { ...p.rule.end } });
  };
  const pickCustom = () => {
    setMode('custom');
    setRule(custom);
  };
  const setEnd = (which, patch) => {
    const end = { ...custom[which], ...patch };
    if (end.base !== 'parent') delete end.column;
    const next = { ...custom, [which]: end };
    setCustom(next);
    setRule(next);
  };

  return (
    <div className="spt-dateeditor">
      <DateTabs tab={tab} setTab={setTab} pickLabel="Pick dates" />

      {tab === 'pick' ? (
        <div>
          <DatePicker mode="range" date={toDate(range.from)} endDate={toDate(range.to)} onDateChange={pickRange} />
          <p className="spt-dateeditor-preview">
            {!range.from
              ? 'Pick a start date, then an end date'
              : `${formatDate(range.from)} – ${range.to ? formatDate(range.to) : 'pick an end date'}`}
          </p>
          {backwards && (
            <p className="mt-1 text-xs text-danger-ink" role="alert">
              The end date can’t be before the start date.
            </p>
          )}
          <div className="flex justify-end">
            <Button kind="tertiary" size="small" leftIcon={CloseSmall} onClick={clearRange}>
              Clear
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="spt-dateeditor-options" role="radiogroup" aria-label="Dynamic timeline">
            {TIMELINE_PRESETS.map((p) => (
              <RadioButton key={p.key} name="spt-dyn-tl" text={p.label} checked={mode === p.key} onSelect={() => pickPreset(p)} />
            ))}
            <RadioButton name="spt-dyn-tl" text="Custom" checked={mode === 'custom'} onSelect={pickCustom} />
            {mode === 'custom' && (
              <div className="spt-dateeditor-grid">
                <span className="spt-dateeditor-key">Start</span>
                <span className="spt-dateeditor-row">
                  {parentColumns ? (
                    <SelectField
                      label="Start counts from"
                      value={baseValue(custom.start)}
                      options={startOptions}
                      width={168}
                      onChange={(v) => setEnd('start', basePatch(v, startOptions))}
                    />
                  ) : (
                    <span className="spt-dateeditor-base">Today</span>
                  )}
                  <OffsetFields label="Start" offset={custom.start.offset} unit={custom.start.unit} onChange={(p) => setEnd('start', p)} />
                </span>
                <span className="spt-dateeditor-key">End</span>
                <span className="spt-dateeditor-row">
                  <SelectField
                    label="End counts from"
                    value={baseValue(custom.end)}
                    options={parentColumns ? endOptions : END_BASE_OPTIONS}
                    width={parentColumns ? 168 : 96}
                    onChange={(v) => {
                      const p = basePatch(v, endOptions);
                      setEnd('end', { ...p, offset: p.base === 'start' && custom.end.offset !== null ? Math.max(0, custom.end.offset) : custom.end.offset });
                    }}
                  />
                  <OffsetFields
                    label="End"
                    offset={custom.end.offset}
                    unit={custom.end.unit}
                    min={custom.end.base === 'start' ? 0 : -MAX_OFFSET}
                    onChange={(p) => setEnd('end', p)}
                  />
                </span>
              </div>
            )}
          </div>
          <DynamicFooter
            lands={lands}
            today={today}
            invalid="Choose whole numbers of days."
            canSet={canSet}
            prefix={preview.prefix}
            note={preview.note}
            onClear={() => {
              onChange(null);
              onClose();
            }}
            onSet={() => {
              onChange({ dynamic: rule });
              onClose();
            }}
          />
        </>
      )}
    </div>
  );
}

function LinkEditor({ value, onChange }) {
  // The link as typed. A URL that isn't a web address stays on screen with a
  // message, and is never handed on: the cell keeps its last good link.
  const [v, setV] = useState(() => value || { url: '', text: '' });
  const badUrl = Boolean(v.url && v.url.trim()) && !isValidLinkUrl(v.url);
  const set = (part, val) => {
    const next = { ...v, [part]: val };
    setV(next);
    if (next.url && next.url.trim() && !isValidLinkUrl(next.url)) return;
    onChange(next.url || next.text ? next : null);
  };
  return (
    <>
      <div className="spt-field-label">URL</div>
      <TextField size="small" placeholder="https://" value={v.url || ''} onChange={(val) => set('url', val)} autoFocus />
      {badUrl && (
        <p className="mt-1 text-xs text-danger-ink" role="alert">
          Enter a web address, like example.com.
        </p>
      )}
      <div className="spt-field-label">Text to display</div>
      <TextField size="small" value={v.text || ''} onChange={(val) => set('text', val)} />
    </>
  );
}

/**
 * Rating: one clickable symbol per step, using THIS column's symbol, count and
 * colour from its board settings. Clicking the current rating clears it, which
 * is how monday's own rating cell behaves.
 */
function RatingEditor({ column, value, onChange, onClose }) {
  const { limit, symbol, color } = ratingSettings(column);
  return (
    <>
      <div className="spt-ratingpicker">
        {Array.from({ length: limit }, (_, i) => {
          const step = i + 1;
          const filled = !!value && step <= value;
          return (
            <button
              key={step}
              type="button"
              className="spt-ratingbtn"
              aria-label={`${step} of ${limit}`}
              onClick={() => {
                onChange(value === step ? null : step);
                onClose();
              }}
            >
              <RatingSymbol symbol={symbol} color={color} filled={filled} size={22} />
            </button>
          );
        })}
      </div>
      <Button
        kind="tertiary"
        size="small"
        leftIcon={CloseSmall}
        onClick={() => {
          onChange(null);
          onClose();
        }}
      >
        Clear
      </Button>
    </>
  );
}

/**
 * Checkbox cells normally toggle straight from the grid (TemplateEditor
 * intercepts the click), so this exists as a correct fallback rather than
 * letting a checkbox fall through to a text box.
 */
function CheckboxEditor({ value, onChange, onClose }) {
  return (
    <div className="spt-menu-list">
      <div className="spt-menu-row spt-menu-row-check">
        <Checkbox
          className="spt-checkbox"
          checkboxClassName="spt-checkbox-box"
          checked={!!value}
          onChange={() => {
            onChange(!value ? true : null);
            onClose();
          }}
          label="Checked"
        />
      </div>
    </div>
  );
}

/** Text and numbers: the cell follows each keystroke; Enter closes the editor. */
function TextEditor({ column, value, onChange, onClose }) {
  return (
    <TextField
      type={canonicalType(column.type) === 'numbers' ? 'number' : 'text'}
      size="small"
      inputAriaLabel={column.title}
      value={value === null || value === undefined ? '' : String(value)}
      onChange={(v) => onChange(v === '' ? null : v)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClose();
      }}
      autoFocus
    />
  );
}

// Types whose picker is built from the board's column settings — labels, options,
// rating scale. Every other type edits from the stored value alone.
const NEEDS_BOARD_SETTINGS = new Set(['status', 'dropdown', 'rating']);

export default function CellEditor({
  anchorEl,
  column,
  boardLoading, // the board (and so the column's settings) is still being read
  boardError, // the board could not be read
  users,
  usersError,
  usersLoading,
  onNeedUsers,
  files, // lib/files service (upload, remove, usage, downloadUrl) for File columns
  uploads, // the editor's lib/cellUploads store, for File columns
  cell, // this cell as the editor describes it to `uploads`
  parentColumns, // the parent item's board columns; given, the date editors offer the parent's columns
  parent, // the parent item for previews (lib/parentItem), or null when there is none yet
  value,
  onChange,
  onClose,
}) {
  const kind = canonicalType(column.type);
  let body;
  // A picker opened before the board arrives would show "not on the current
  // board" for a column that IS on it — wait instead. Once the board is in, a
  // column with no settings really is unmatched, and the editors say so.
  if (NEEDS_BOARD_SETTINGS.has(kind) && !column.settings && (boardLoading || boardError)) {
    body = boardLoading ? (
      <div className="spt-menu-empty flex items-center gap-2" role="status">
        <Spinner />
        Reading your board columns and column settings
      </div>
    ) : (
      <div className="spt-menu-empty" role="alert">
        Could not read this board's subitem columns, so this column's choices are unavailable.
        Refresh to try again.
      </div>
    );
    return (
      <AnchoredPopover anchorEl={anchorEl} onClose={onClose} width={230}>
        {body}
      </AnchoredPopover>
    );
  }
  switch (kind) {
    case 'status':
      body = <StatusEditor column={column} value={value} onChange={onChange} onClose={onClose} />;
      break;
    case 'people':
      body = (
        <PeopleEditor
          users={users}
          usersError={usersError}
          usersLoading={usersLoading}
          onNeedUsers={onNeedUsers}
          value={value}
          onChange={onChange}
          onClose={onClose}
          parentColumns={parentColumns}
          parent={parent}
        />
      );
      break;
    case 'dropdown':
      body = <DropdownEditor column={column} value={value} onChange={onChange} />;
      break;
    case 'date':
      body = <DateEditor value={value} onChange={onChange} onClose={onClose} parentColumns={parentColumns} parent={parent} />;
      break;
    case 'timeline':
      body = <TimerangeEditor value={value} onChange={onChange} onClose={onClose} parentColumns={parentColumns} parent={parent} />;
      break;
    case 'link':
      body = <LinkEditor value={value} onChange={onChange} />;
      break;
    case 'checkbox':
      body = <CheckboxEditor value={value} onChange={onChange} onClose={onClose} />;
      break;
    case 'rating':
      body = <RatingEditor column={column} value={value} onChange={onChange} onClose={onClose} />;
      break;
    case 'file':
      body = <FileCellEditor files={files} uploads={uploads} cell={cell} value={value} onChange={onChange} />;
      break;
    default:
      body = <TextEditor column={column} value={value} onChange={onChange} onClose={onClose} />;
  }

  // AnchoredPopover positions with arithmetic, so width must stay numeric.
  // Rating widens with the column's own symbol count.
  const width =
    kind === 'people'
      ? parentColumns ? 360 : 260 // the Dynamic tab's Column select beside its label, and its preview line
      : kind === 'file'
        ? 320 // a file name, its size and two buttons per row
      : kind === 'rating'
        ? Math.max(180, 48 + ratingSettings(column).limit * 30)
        : kind === 'date'
          ? parentColumns ? 400 : 340 // the Dynamic tab's "( ) Today [+n] [working days]" row; the parent part's preview line with a parent
          : kind === 'timeline'
            ? parentColumns ? 500 : 440 // the Dynamic tab's "End [Start] [+n] [working days]" row, indented under Custom; wider bases with a parent
            : 230;
  return (
    <AnchoredPopover anchorEl={anchorEl} onClose={onClose} width={width}>
      {body}
    </AnchoredPopover>
  );
}
